import { queryCollectionOptions } from "@tanstack/query-db-collection";
import {
    collectionOptions,
    type DbClient,
    type LiveQueryOptions,
    useDbClient,
    useLiveQuery,
} from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { getSavedListingIds, saveListing, unsaveListing } from "./commands";
import type { SavedListingReference } from "./saved-listing.contract";

export const SAVED_LISTINGS_QUERY_KEY: readonly ["savedListingIds"] = [
    "savedListingIds",
];
export const UNDO_WINDOW_MS = 5000;

export class FavoriteUndoCancelledError extends Error {
    constructor() {
        super("Favorite unfavorite was cancelled");
        this.name = "FavoriteUndoCancelledError";
    }
}

const pendingUnfavoriteControllersByClient = new WeakMap<
    DbClient,
    Map<string, AbortController>
>();
const savedListingGenerationByClient = new WeakMap<DbClient, number>();

function getPendingUnfavoriteControllers(dbClient: DbClient) {
    const existing = pendingUnfavoriteControllersByClient.get(dbClient);
    if (existing) return existing;

    const controllers = new Map<string, AbortController>();
    pendingUnfavoriteControllersByClient.set(dbClient, controllers);
    return controllers;
}

function waitForUndoWindow(dbClient: DbClient, listingId: string) {
    const pendingUnfavoriteControllers =
        getPendingUnfavoriteControllers(dbClient);
    const controller = new AbortController();
    pendingUnfavoriteControllers.set(listingId, controller);

    return new Promise<void>((resolve, reject) => {
        const clearPendingController = () => {
            if (pendingUnfavoriteControllers.get(listingId) === controller) {
                pendingUnfavoriteControllers.delete(listingId);
            }
        };

        const timer = setTimeout(() => {
            clearPendingController();
            resolve();
        }, UNDO_WINDOW_MS);

        controller.signal.addEventListener(
            "abort",
            () => {
                clearTimeout(timer);
                clearPendingController();
                reject(
                    controller.signal.reason instanceof Error
                        ? controller.signal.reason
                        : new FavoriteUndoCancelledError(),
                );
            },
            { once: true },
        );
    });
}

export function cancelPendingUnfavorite(dbClient: DbClient, listingId: string) {
    const pendingUnfavoriteControllers =
        getPendingUnfavoriteControllers(dbClient);
    const controller = pendingUnfavoriteControllers.get(listingId);
    if (!controller) {
        return false;
    }

    controller.abort(new FavoriteUndoCancelledError());
    return true;
}

export function resetSavedListingIds(dbClient: DbClient) {
    savedListingGenerationByClient.set(
        dbClient,
        (savedListingGenerationByClient.get(dbClient) ?? 0) + 1,
    );
    for (const controller of getPendingUnfavoriteControllers(
        dbClient,
    ).values()) {
        controller.abort(new FavoriteUndoCancelledError());
    }
    dbClient
        .requireDependency<QueryClient>("queryClient")
        .setQueryData([...SAVED_LISTINGS_QUERY_KEY], []);
}

export const savedListingIdsCollection = collectionOptions(
    "savedListingIds",
    (dbClient) => {
        const queryClient =
            dbClient.requireDependency<QueryClient>("queryClient");
        queryClient.setQueryDefaults([...SAVED_LISTINGS_QUERY_KEY], {
            refetchOnWindowFocus: false,
        });

        return queryCollectionOptions<SavedListingReference>({
            id: "savedListingIds",
            queryKey: [...SAVED_LISTINGS_QUERY_KEY],
            queryClient,
            getKey: (item) => item.listingId,

            staleTime: 30_000,
            queryFn: async () => await getSavedListingIds(),
            onInsert: async ({ transaction, collection }) => {
                const generation =
                    savedListingGenerationByClient.get(dbClient) ?? 0;
                await Promise.all(
                    transaction.mutations.map(async (mutation) => {
                        await saveListing({
                            data: { listingId: String(mutation.key) },
                        });
                    }),
                );
                if (
                    generation !==
                    (savedListingGenerationByClient.get(dbClient) ?? 0)
                ) {
                    return { refetch: false };
                }

                collection.utils.writeInsert(
                    transaction.mutations.map((mutation) => mutation.modified),
                );
                return { refetch: false };
            },
            onDelete: async ({ transaction }) => {
                await Promise.all(
                    transaction.mutations.map(async (mutation) => {
                        const listingId = String(mutation.key);
                        await waitForUndoWindow(dbClient, listingId);
                        await unsaveListing({ data: { listingId } });
                    }),
                );
            },
        });
    },
);

export function getSavedListingIdsCollection(dbClient: DbClient) {
    return dbClient.collection<SavedListingReference>(
        savedListingIdsCollection,
    );
}

export function preloadSavedListingIdsOnClient(dbClient: DbClient) {
    if (typeof window === "undefined") return;
    return getSavedListingIdsCollection(dbClient)
        .preload()
        .catch(() => {});
}

const savedListingIdsLiveQuery = {
    queryKey: ["savedListingIds", "ordered"],
    query: (q) =>
        q
            .from({ saved: savedListingIdsCollection })
            .orderBy(({ saved }) => saved.savedAt, "desc"),
} satisfies LiveQueryOptions;

export function useSavedListingIds() {
    return useLiveQuery(savedListingIdsLiveQuery);
}

export type SavedListingSourceStatus = "loading" | "error" | "success";

function getSavedListingSourceStatus(
    queryClient: QueryClient,
): SavedListingSourceStatus {
    const status = queryClient.getQueryState([
        ...SAVED_LISTINGS_QUERY_KEY,
    ])?.status;
    if (status === "success" || status === "error") return status;
    return "loading";
}

export function useSavedListingSourceStatus(queryClient: QueryClient) {
    return useSyncExternalStore<SavedListingSourceStatus>(
        (listener) =>
            queryClient.getQueryCache().subscribe((event) => {
                if (
                    event.query.queryKey.length ===
                        SAVED_LISTINGS_QUERY_KEY.length &&
                    event.query.queryKey[0] === SAVED_LISTINGS_QUERY_KEY[0]
                ) {
                    listener();
                }
            }),
        () => getSavedListingSourceStatus(queryClient),
        () => "loading",
    );
}

export function resolveSavedListingIdsSet({
    savedListingIdsQuery,
    sourceStatus,
}: {
    savedListingIdsQuery: {
        data: ReadonlyArray<SavedListingReference> | undefined;
        isError: boolean;
        isLoading: boolean;
    };
    sourceStatus: SavedListingSourceStatus;
}) {
    if (
        savedListingIdsQuery.isError ||
        savedListingIdsQuery.isLoading ||
        sourceStatus !== "success" ||
        savedListingIdsQuery.data === undefined
    )
        return undefined;
    return new Set(
        savedListingIdsQuery.data.map((savedListing) => savedListing.listingId),
    );
}

export function useSavedListingIdsSet() {
    const dbClient = useDbClient();
    const queryClient = dbClient.requireDependency<QueryClient>("queryClient");
    const query = useSavedListingIds();
    const sourceStatus = useSavedListingSourceStatus(queryClient);

    return {
        ...query,
        sourceStatus,
        state: resolveSavedListingIdsSet({
            savedListingIdsQuery: query,
            sourceStatus,
        }),
    };
}

export async function retrySavedListingIds(dbClient: DbClient) {
    const collection = getSavedListingIdsCollection(dbClient);
    const queryClient = dbClient.requireDependency<QueryClient>("queryClient");
    await queryClient.invalidateQueries({
        queryKey: [...SAVED_LISTINGS_QUERY_KEY],
    });
    await collection.preload();
}

export function useRetrySavedListingIds() {
    const dbClient = useDbClient();
    return () => retrySavedListingIds(dbClient);
}

export function useIsListingSaved(listingId: string): boolean | undefined {
    const { state, isLoading } = useSavedListingIdsSet();
    if (isLoading || !state) return undefined;
    return state.has(listingId);
}
